import type { ValueFormatterParams } from 'ag-grid-community';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import { isPlainObject } from 'lodash-es';
import { EMPTY_OBJECT } from '../../../utils';
import { logger } from '../../../utils/logger';
import { AgGridText, type AgGridTextParams } from '../../AgGrid/AgGridText';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';
import { getFormattedCellValueColumnComparator } from './utils';

export type TextColumnParams = {
  /** Whether to suppress ellipsizing the text if overflowing, defaults to false */
  suppressEllipsize?: boolean;
  /** Keep CSS ellipsis but do not show EllipsisBoxWithTooltip's overflow tooltip. */
  suppressOverflowTooltip?: boolean;
  /** Text Override based on Params data - an override of ag-grid valueFormatter */
  getLabel?: (value: any, params: ValueFormatterParams<any, any>) => string;
  /** Placeholder text when no text shown */
  placeholder?: string;
  color?: string;
  /** Function to get href (internal routing) for link navigation, uses React Router's Link component to route to the href */
  getHref?: (params: ICellRendererParams & TextColumnParams) => string | undefined;
};

/** Shared string formatting for `text` and `textLiveEdit` columns. */
export function formatTextCellValue(params: ValueFormatterParams, columnParams: TextColumnParams): string {
  const { value } = params;
  if (columnParams.getLabel) {
    return columnParams.getLabel(value, params) ?? '';
  }

  if (value == null) {
    return '';
  }

  if (isPlainObject(value) || Array.isArray(value)) {
    try {
      return JSON.stringify(value);
    } catch (e) {
      logger.error(e as Error);
      return '';
    }
  }
  return `${value}`;
}

/** Simple text column supporting auto-ellipsizing and default placeholder behavior for editors */
export const text: ColDefFactory<Column<TextColumnParams>> = column => {
  const columnParams = column.params ?? EMPTY_OBJECT;

  return {
    ...baseColumn(column),
    ...(columnParams.getLabel != null ? { comparator: getFormattedCellValueColumnComparator(column) } : {}),
    cellEditor: 'input',
    valueFormatter: (params: ValueFormatterParams): string => formatTextCellValue(params, columnParams),
    cellRenderer: AgGridText,
    cellRendererParams: {
      columnParams,
    } satisfies AgGridTextParams,
  };
};
